import React, { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import { Clock, CheckCircle2, XCircle, RefreshCw, Loader2, Zap, Calendar } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CRON_ICONS = {
  'agenda-wa-reminders': Calendar,
  'crm-weekly-followup': Clock,
};
const CRON_COLORS = {
  'agenda-wa-reminders': 'text-violet-400',
  'crm-weekly-followup': 'text-emerald-400',
};

const fmtDate = (iso) => {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleString('es-ES', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }); }
  catch { return iso; }
};

const fmtRel = (iso) => {
  if (!iso) return '';
  try {
    const diff = Math.floor((new Date(iso).getTime() - Date.now()) / 1000);
    if (diff <= 0) return 'próximamente';
    const h = Math.floor(diff / 3600);
    const m = Math.floor((diff % 3600) / 60);
    return h > 0 ? `en ${h}h ${m}m` : `en ${m} min`;
  } catch { return ''; }
};

export default function CronLogsPanel({ authFetch }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const apiFn = authFetch || ((url, opts) => fetch(url, { ...opts, credentials: 'include' }));

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await apiFn(`${API}/admin/cron-logs`);
      setData(await res.json());
    } catch { toast.error('Error al cargar logs de crons'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const logs = data?.logs || [];
  const schedule = data?.schedule || [];
  const stats = data?.stats || {};

  return (
    <div className="space-y-5" data-testid="cron-logs-panel">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-text-heading font-heading text-lg font-semibold flex items-center gap-2">
            <Zap className="w-5 h-5 text-primary" /> Panel de Crons
          </h3>
          <p className="text-sm text-text-muted mt-0.5">Historial de ejecuciones y próximas tareas programadas.</p>
        </div>
        <button onClick={load} disabled={loading} className="btn-secondary !px-3 !py-2" data-testid="cron-refresh">
          <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
        </button>
      </div>

      {/* Próximas ejecuciones */}
      <div className="grid sm:grid-cols-2 gap-3">
        {schedule.map((cron) => {
          const Icon = CRON_ICONS[cron.name] || Clock;
          const color = CRON_COLORS[cron.name] || 'text-primary';
          const s = stats.by_cron?.[cron.name] || {};
          return (
            <div key={cron.name} className="glass-card p-4 space-y-3" data-testid={`cron-card-${cron.name}`}>
              <div className="flex items-center gap-2">
                <Icon className={`w-4 h-4 ${color}`} />
                <p className="text-sm font-semibold text-text-heading">{cron.description}</p>
              </div>
              <p className="text-xs text-text-muted">{cron.schedule}</p>
              <div className="flex items-center justify-between">
                <span className="text-[10px] px-2 py-1 rounded-full bg-primary/10 text-primary border border-primary/20">
                  Próxima: {fmtRel(cron.next)}
                </span>
                <div className="flex gap-3 text-[10px] text-text-muted">
                  <span><span className="text-emerald-400 font-bold">{s.sent ?? 0}</span> enviados</span>
                  {(s.errors ?? 0) > 0 && <span><span className="text-red-400 font-bold">{s.errors}</span> errores</span>}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Historial */}
      <div className="glass-card overflow-hidden">
        <div className="px-5 py-3.5 border-b border-border-subtle flex items-center justify-between">
          <p className="text-sm font-semibold text-text-heading">Historial de ejecuciones</p>
          <span className="text-[10px] text-text-muted">{logs.length} registros</span>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-8 text-text-muted">
            <Loader2 className="w-5 h-5 animate-spin mr-2" />Cargando…
          </div>
        ) : logs.length === 0 ? (
          <p className="text-center py-8 text-sm text-text-muted">Sin ejecuciones aún</p>
        ) : (
          <div className="divide-y divide-border-subtle">
            {logs.map((log, i) => {
              const Icon = CRON_ICONS[log.cron] || Clock;
              const color = CRON_COLORS[log.cron] || 'text-primary';
              const hasSent = (log.sent ?? 0) > 0 || (log.stale_contacts ?? 0) > 0 || (log.events_found ?? 0) > 0;
              const hasError = (log.errors ?? 0) > 0;
              return (
                <motion.div key={i} initial={{ opacity: 0 }} animate={{ opacity: 1 }}
                  className="flex items-center gap-4 px-5 py-3" data-testid={`cron-log-${i}`}>
                  <Icon className={`w-3.5 h-3.5 flex-shrink-0 ${color}`} />
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-medium text-text-body">{log.cron}</p>
                    <p className="text-[10px] text-text-muted">{fmtDate(log.ran_at)}</p>
                  </div>
                  <div className="flex items-center gap-3 text-[10px]">
                    {log.events_found !== undefined && <span className="text-text-muted">{log.events_found} eventos</span>}
                    {log.stale_contacts !== undefined && <span className="text-text-muted">{log.stale_contacts} inactivos</span>}
                    {(log.sent ?? 0) > 0 && <span className="text-emerald-400 font-semibold">{log.sent} enviados</span>}
                    {hasError && <span className="text-red-400">{log.errors} err</span>}
                    {hasSent
                      ? <CheckCircle2 className="w-3.5 h-3.5 text-emerald-400" />
                      : <CheckCircle2 className="w-3.5 h-3.5 text-text-muted/40" />}
                  </div>
                </motion.div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
